import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, CalendarDays, Truck, MapPin, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { Button } from "./ui/button";
import { useCart } from "../contexts/CartContext";

// Swipeable image hook for touch + mouse drag
function useSwipe(imageCount, currentImg, setCurrentImg) {
  const ref = useRef(null);
  const startX = useRef(0);
  const isDragging = useRef(false);
  const threshold = 50;

  const handleStart = useCallback((clientX) => { startX.current = clientX; isDragging.current = true; }, []);
  const handleEnd = useCallback((clientX) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = startX.current - clientX;
    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentImg < imageCount - 1) setCurrentImg(currentImg + 1);
      else if (diff < 0 && currentImg > 0) setCurrentImg(currentImg - 1);
    }
  }, [currentImg, imageCount, setCurrentImg]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onTouchStart = (e) => handleStart(e.touches[0].clientX);
    const onTouchEnd = (e) => handleEnd(e.changedTouches[0].clientX);
    const onMouseDown = (e) => { e.preventDefault(); handleStart(e.clientX); };
    const onMouseUp = (e) => handleEnd(e.clientX);
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [handleStart, handleEnd]);

  return ref;
}

/**
 * CTLQuickView — "Complete the Look" dedicated QuickView
 *
 * Different from PLP QuickView:
 * - Shows read-only inherited booking params (event date, return date, duration, delivery)
 * - No calendar, no duration picker, no delivery selector
 * - Only allows variation selection
 * - "Add to Bundle" adds item and closes
 * - "View More" navigates to PDP with locked fields
 */
export default function CTLQuickView({ outfit, isOpen, onClose, onAddBundle, bookingParams, parentOutfitId }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [currentImg, setCurrentImg] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);

  const images = outfit?.images?.length > 0 ? outfit.images : [];
  const desktopSwipeRef = useSwipe(images.length, currentImg, setCurrentImg);
  const mobileSwipeRef = useSwipe(images.length, currentImg, setCurrentImg);

  useEffect(() => {
    if (isOpen && outfit) {
      setCurrentImg(0);
      setSelectedOption(outfit.rental_options?.[0] || null);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen, outfit?.id]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen || !outfit) return null;

  const { eventDate, returnDate, duration, deliveryMethod } = bookingParams || {};

  // Price based on inherited duration
  const getPrice = (opt) => {
    if (opt) {
      if (duration <= 4 && opt.price_4_day > 0) return opt.price_4_day;
      if (duration > 4 && opt.price_8_day > 0) return opt.price_8_day;
      return opt.price_4_day || opt.price_per_day || 0;
    }
    if (duration <= 4 && outfit.price_4_day > 0) return outfit.price_4_day;
    if (duration > 4 && outfit.price_8_day > 0) return outfit.price_8_day;
    return outfit.price_4_day || outfit.price_per_day || 0;
  };

  const rentalPrice = getPrice(selectedOption);

  // Bond calculation
  const getBond = () => {
    if (outfit.bond_type === "percentage" && outfit.bond_value > 0)
      return Math.round(rentalPrice * outfit.bond_value / 100);
    return outfit.bond_value || outfit.deposit_amount || 0;
  };
  const bondAmount = getBond();

  const handleAddToBundle = async () => {
    const { eventDate: ed, duration: dur, deliveryMethod: dm } = bookingParams || {};
    const edStr = ed instanceof Date ? ed.toISOString().split('T')[0] : (typeof ed === 'string' ? ed : '');
    await addToCart({
      outfit_id: outfit.id,
      event_date: edStr,
      duration: dur,
      delivery_method: dm,
      rental_option_id: selectedOption?.id || null,
      rental_option_name: selectedOption?.name || null,
      is_addon: true,
      parent_cart_item_id: null,
      parent_outfit_id: parentOutfitId || null,
    });
    // Also add to local bundle for summary display
    if (onAddBundle) {
      onAddBundle({
        ...outfit,
        bundleDuration: dur,
        bundleOption: selectedOption,
      }, true);
    }
    onClose();
  };

  const handleViewMore = () => {
    sessionStorage.setItem("mahra_locked_booking", JSON.stringify({
      eventDate: eventDate instanceof Date ? eventDate.toISOString() : eventDate,
      duration,
      deliveryMethod,
      parentOutfitId: parentOutfitId || null,
    }));
    onClose();
    navigate(`/outfit/${outfit.id}`);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const date = d instanceof Date ? d : new Date(d);
    return format(date, "EEE, MMM d");
  };

  // Content shared between desktop and mobile
  const renderDetails = () => (
    <div className="flex-1 flex flex-col">
      {/* Name */}
      <h2 className="text-xl text-[#1A1A1A] leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }} data-testid="ctl-qv-name">
        {outfit.name}
      </h2>

      {/* Pricing */}
      <div className="mt-3" data-testid="ctl-qv-pricing">
        <span className="text-[13px] text-[#5A5A5A]">Rental </span>
        <span className="text-xl text-[#9A7B4F] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          ${rentalPrice}
        </span>
        <span className="text-[12px] text-[#676664]/50 ml-1.5">/ {duration} days</span>
        {outfit.is_addon && outfit.addon_discount_percent > 0 && (
          <span className="inline-block ml-2 px-2 py-0.5 bg-[#9A7B4F]/10 text-[#9A7B4F] text-[10px] font-medium rounded tracking-wide" data-testid="ctl-qv-addon-discount">
            {outfit.addon_discount_percent}% OFF AS ADD-ON
          </span>
        )}
      </div>

      {/* Bond */}
      <div className="mt-1.5" data-testid="ctl-qv-bond">
        <span className="text-[13px] text-[#5A5A5A]">Security Bond* </span>
        <span className="text-[14px] text-[#1A1A1A] font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          ${bondAmount}
        </span>
        <span className="text-[11px] text-[#9A7B4F] ml-1.5">Fully Redeemable</span>
      </div>

      {/* Description */}
      {outfit.description && (
        <p className="text-[13px] text-[#5A5A5A] mt-3 leading-relaxed line-clamp-3" data-testid="ctl-qv-description">
          {outfit.description}
        </p>
      )}

      {/* Variation Selector */}
      {outfit.rental_options?.length > 1 && (
        <div className="mt-4" data-testid="ctl-qv-variations">
          <p className="text-[10px] tracking-[0.12em] uppercase text-[#7A7A7A] mb-2 font-medium">Choose From</p>
          <div className="flex flex-wrap gap-2">
            {outfit.rental_options.map(opt => (
              <button
                key={opt.id}
                onClick={() => setSelectedOption(opt)}
                className={`px-4 py-2 text-[12px] border rounded transition-all ${
                  selectedOption?.id === opt.id
                    ? 'border-[#9A7B4F]/50 bg-[#F5F0E6] text-[#1A1A1A] font-medium'
                    : 'border-[#282828]/8 bg-white text-[#5A5A5A] hover:border-[#9A7B4F]/30'
                }`}
                data-testid={`ctl-qv-option-${opt.id}`}
              >
                {opt.name}
                <span className="text-[10px] text-[#9A7B4F] ml-1.5">${getPrice(opt)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1" />

      {/* Locked Booking Info */}
      <div className="bg-white border border-[#282828]/8 p-3.5 mt-4" data-testid="ctl-qv-locked-info">
        <div className="flex items-center gap-1.5 mb-3">
          <Lock className="w-3 h-3 text-[#9A7B4F]" strokeWidth={1.5} />
          <span className="text-[9px] tracking-[0.12em] uppercase text-[#9A7B4F] font-medium">Inherited Booking Details</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] text-[#676664]/50">Event Date</p>
            <p className="text-[13px] text-[#282828] font-medium mt-0.5">{formatDate(eventDate)}</p>
          </div>
          <div>
            <p className="text-[10px] text-[#676664]/50">Return Date</p>
            <p className="text-[13px] text-[#282828] font-medium mt-0.5">{formatDate(returnDate)}</p>
          </div>
          <div>
            <p className="text-[10px] text-[#676664]/50">Duration</p>
            <p className="text-[13px] text-[#282828] font-medium mt-0.5">{duration} days</p>
          </div>
          <div>
            <p className="text-[10px] text-[#676664]/50">Delivery</p>
            <p className="text-[13px] text-[#282828] font-medium mt-0.5 flex items-center gap-1">
              {deliveryMethod === "delivery" ? (
                <><Truck className="w-3 h-3 text-[#9A7B4F]" strokeWidth={1.5} />Local Delivery</>
              ) : (
                <><MapPin className="w-3 h-3 text-[#9A7B4F]" strokeWidth={1.5} />Pick Up</>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="mt-4 space-y-2">
        <Button
          onClick={handleAddToBundle}
          className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-4 text-[10px] tracking-[0.15em] uppercase"
          data-testid="ctl-qv-add-bundle-btn"
        >
          {outfit.is_addon && outfit.addon_discount_percent > 0
            ? `Add to Bundle – $${Math.round(rentalPrice * (100 - outfit.addon_discount_percent) / 100)} (${outfit.addon_discount_percent}% off)`
            : `Add to Bundle – $${rentalPrice}`
          }
        </Button>
        <Button
          onClick={handleViewMore}
          variant="outline"
          className="w-full border-[#282828]/15 text-[#676664] py-3 text-[10px] tracking-[0.12em] uppercase hover:border-[#9A7B4F]/30"
          data-testid="ctl-qv-view-more-btn"
        >
          View More
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/25 z-50" onClick={onClose} data-testid="ctl-qv-backdrop" />

      {/* DESKTOP */}
      <div className="fixed z-50 hidden md:flex top-0 right-0 h-screen w-[58%] lg:w-[50%] xl:w-[44%] bg-[#FAF8F0]" data-testid="ctl-qv-panel">
        <button onClick={onClose} className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white transition-colors" data-testid="ctl-qv-close">
          <X className="w-4 h-4 text-[#282828]" strokeWidth={1.5} />
        </button>

        {/* Image — swipeable */}
        <div className="w-[58%] flex-shrink-0 relative bg-[#f5f3f0] select-none cursor-grab active:cursor-grabbing" ref={desktopSwipeRef} data-testid="ctl-qv-image-swipe">
          {images.map((img, i) => (
            <img
              key={i}
              src={img}
              alt={outfit.name}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
              style={{
                opacity: i === currentImg ? 1 : 0,
                objectPosition: `${(outfit.image_focal_points || {})[img]?.x ?? 50}% ${(outfit.image_focal_points || {})[img]?.y ?? 50}%`
              }}
            />
          ))}
          {images.length > 1 && (
            <>
              <button onClick={() => setCurrentImg(i => Math.max(0, i - 1))} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/70 flex items-center justify-center hover:bg-white z-10">
                <ChevronLeft className="w-4 h-4 text-[#676664]" strokeWidth={1.5} />
              </button>
              <button onClick={() => setCurrentImg(i => Math.min(images.length - 1, i + 1))} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/70 flex items-center justify-center hover:bg-white z-10">
                <ChevronRight className="w-4 h-4 text-[#676664]" strokeWidth={1.5} />
              </button>
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 z-10">
                {images.map((_, i) => (
                  <button key={i} onClick={() => setCurrentImg(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentImg ? "bg-[#9A7B4F] w-4" : "bg-[#282828]/15"}`} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col h-full overflow-y-auto px-5 pt-5 pb-4">
          {renderDetails()}
        </div>
      </div>

      {/* MOBILE */}
      <div className="fixed z-50 bg-[#FAF8F0] md:hidden inset-0 overflow-y-auto" data-testid="ctl-qv-panel-mobile">
        <button onClick={onClose} className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center bg-white/80" data-testid="ctl-qv-close-mobile">
          <X className="w-4 h-4 text-[#282828]" strokeWidth={1.5} />
        </button>
        {images.length > 0 && (
          <div className="relative bg-[#f5f3f0] select-none" style={{ height: "55vh" }} ref={mobileSwipeRef} data-testid="ctl-qv-mobile-image-swipe">
            {images.map((img, i) => (
              <img key={i} src={img} alt={outfit.name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" style={{ opacity: i === currentImg ? 1 : 0, objectPosition: `${(outfit.image_focal_points || {})[img]?.x ?? 50}% ${(outfit.image_focal_points || {})[img]?.y ?? 50}%` }} />
            ))}
            {images.length > 1 && (
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10">
                {images.map((_, i) => (
                  <button key={i} onClick={() => setCurrentImg(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentImg ? "bg-[#9A7B4F] w-4" : "bg-[#282828]/15"}`} />
                ))}
              </div>
            )}
          </div>
        )}
        <div className="px-5 py-5">
          {renderDetails()}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes slideInUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        [data-testid="ctl-qv-panel"] { animation: slideInRight 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        [data-testid="ctl-qv-panel-mobile"] { animation: slideInUp 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </>
  );
}
